import React, { useEffect, useMemo, useState } from 'react';

type Props = {
  colors: any;
  buildUrlWithFilters: (base: string) => string;
  buildPostBody?: () => any; // parent passes {start,end,...}
};

type Summary = {
  wait_safety_h: number;
  approve_to_delivery_h: number;
  execution_h: number;
};

type TrendPoint = {
  date: string; // yyyy-mm-dd (bucket label)
  wait_safety_h: number;
  approve_to_delivery_h: number;
  execution_h: number;
};

// ---------- Persian digits helper ----------
const toFaDigits = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
    .replace(/\./g, '٫')
    .replace(/,/g, '٬');
};

const ResponseTimeAveragesCard: React.FC<Props> = ({ colors, buildUrlWithFilters, buildPostBody }) => {
  const [summary, setSummary] = useState<Summary>({ wait_safety_h: 0, approve_to_delivery_h: 0, execution_h: 0 });
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ---- compact tokens
  const S = {
    radius: 14,
    padCard: 12,
    padBox: 8,
    gap: 8,
    fsTitle: 14,
    fsSub: 11,
    fsKpiLabel: 11,
    fsKpiVal: 20,
    chartH: 200,
  };

  // ---- styles
  const card: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: S.radius,
    padding: S.padCard,
    boxShadow: '0 4px 14px rgba(0,0,0,.18)',
  };
  const title: React.CSSProperties = { fontWeight: 800, marginBottom: 6, letterSpacing: 0.2, fontSize: S.fsTitle };
  const sub: React.CSSProperties = { fontSize: S.fsSub, color: colors.sub, marginBottom: 8 };

  const kpiWrap: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: S.gap,
    marginBottom: 8,
  };
  const kpiBox: React.CSSProperties = {
    background: '#0f172a',
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: S.padBox,
  };
  const kpiLabel: React.CSSProperties = { fontSize: S.fsKpiLabel, color: colors.sub, marginBottom: 4 };
  const kpiVal: React.CSSProperties = { fontSize: 16, fontWeight: 900, lineHeight: 1.1 };

  const chartWrap: React.CSSProperties = {
    background: '#0f172a',
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: 6,
    minHeight: S.chartH,
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr(null);

      const token = localStorage.getItem('token') || '';
      const headers: HeadersInit = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const endpoint = '/api/dashboard/operation/avg-time';

      // helpers
      const toRFC3339 = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');
      const startOfDayUTC = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0));
      const endOfDayUTC   = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59));
      const startOfWeekUTC = (d: Date) => { const z = startOfDayUTC(d); return new Date(z.getTime() - (z.getUTCDay() * 86400000)); }; // Sun-start
      const endOfWeekUTC   = (d: Date) => { const s = startOfWeekUTC(d); return endOfDayUTC(new Date(s.getTime() + 6 * 86400000)); };
      const startOfMonthUTC= (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0));
      const endOfMonthUTC  = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 23, 59, 59));

      const ASSUME_SECONDS = true;
      const toHours = (v: number) => (ASSUME_SECONDS ? v / 60 : v);

      const overallBody = (() => {
        if (buildPostBody) return buildPostBody();
        const now = new Date();
        const s = new Date(now.getTime() - 7 * 86400000);
        return { start: toRFC3339(startOfDayUTC(s)), end: toRFC3339(endOfDayUTC(now)) };
      })();

      // 1) summary
      try {
        const r = await fetch(endpoint, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(overallBody) });
        const t = await r.text();
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${t}`);
        const j = t ? JSON.parse(t) : {};
        setSummary({
          wait_safety_h:        toHours(Number(j?.average_spent_to_approve ?? 0)),
          approve_to_delivery_h:toHours(Number(j?.average_time_to_get_spades ?? 0)),
          execution_h:          toHours(Number(j?.average_time_to_finish ?? 0)),
        });
      } catch (e: any) {
        setErr(e?.message || 'خطا در دریافت خلاصه');
        setSummary({ wait_safety_h: 0, approve_to_delivery_h: 0, execution_h: 0 });
      }

      // 2) trend (build buckets, call same endpoint per bucket)
      try {
        const start = new Date(overallBody.start);
        const end   = new Date(overallBody.end);
        const oneDay = 86400000;

        const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / oneDay) + 1);
        const group: 'day' | 'week' | 'month' =
          totalDays <= 31 ? 'day' : totalDays <= 180 ? 'week' : 'month';

        type Bucket = { label: string; bStart: Date; bEnd: Date };
        const buckets: Bucket[] = [];

        if (group === 'day') {
          let cur = startOfDayUTC(start);
          while (cur <= end) {
            const bStart = startOfDayUTC(cur);
            const bEnd   = endOfDayUTC(cur);
            buckets.push({ label: bStart.toISOString().slice(0, 10), bStart, bEnd });
            cur = new Date(cur.getTime() + oneDay);
          }
        } else if (group === 'week') {
          let cur = startOfWeekUTC(start);
          while (cur <= end) {
            const bStart = startOfWeekUTC(cur);
            const bEnd   = endOfWeekUTC(cur);
            buckets.push({ label: bStart.toISOString().slice(0, 10), bStart, bEnd });
            cur = new Date(cur.getTime() + 7 * oneDay);
          }
        } else {
          // month
          let cur = startOfMonthUTC(start);
          while (cur <= end) {
            const bStart = startOfMonthUTC(cur);
            const bEnd   = endOfMonthUTC(cur);
            const y = bStart.getUTCFullYear();
            const m = String(bStart.getUTCMonth() + 1).padStart(2, '0');
            buckets.push({ label: `${y}-${m}-01`, bStart, bEnd });
            cur = new Date(Date.UTC(bStart.getUTCFullYear(), bStart.getUTCMonth() + 1, 1));
          }
        }

        // parallel calls with capped concurrency
        const MAX_CONC = 5;
        const out: TrendPoint[] = new Array(buckets.length);
        let i = 0;
        const worker = async () => {
          while (i < buckets.length) {
            const idx = i++;
            const b = buckets[idx];
            const body = { start: toRFC3339(b.bStart), end: toRFC3339(b.bEnd) };
            try {
              const r = await fetch(endpoint, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
              const t = await r.text();
              if (!r.ok) throw new Error(`HTTP ${r.status}: ${t}`);
              const j = t ? JSON.parse(t) : {};
              out[idx] = {
                date: b.label,
                wait_safety_h:         toHours(Number(j?.average_spent_to_approve ?? 0)),
                approve_to_delivery_h: toHours(Number(j?.average_time_to_get_spades ?? 0)),
                execution_h:           toHours(Number(j?.average_time_to_finish ?? 0)),
              };
            } catch {
              out[idx] = { date: b.label, wait_safety_h: 0, approve_to_delivery_h: 0, execution_h: 0 };
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(MAX_CONC, buckets.length) }, () => worker()));
        // keep order by label (already increasing)
        setTrend(out.filter(Boolean));
      } catch (e: any) {
        setTrend([]);
        setErr((prev) => prev || 'نمودار با API موجود قابل محاسبه نبود.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [buildPostBody, buildUrlWithFilters]);

  // --- chart geometry (smaller)
  const w = 760, h = S.chartH, pad = { t: 8, r: 10, b: 20, l: 30 };
  const { pathSafety, pathDelivery, pathExec, yTicks } = useMemo(() => {
    if (!trend.length) return { pathSafety: '', pathDelivery: '', pathExec: '', yTicks: [] as any[] };
    const vals = trend.flatMap((p) => [p.wait_safety_h, p.approve_to_delivery_h, p.execution_h]);
    const maxVal = Math.max(1, ...vals), minVal = 0;
    const x = (i: number) => pad.l + ((w - pad.l - pad.r) * i) / Math.max(1, trend.length - 1);
    const y = (v: number) => h - pad.b - ((h - pad.t - pad.b) * (v - minVal)) / (maxVal - minVal || 1);
    const line = (arr: number[]) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');
    const ticks = 4;
    const yt = Array.from({ length: ticks + 1 }).map((_, i) => {
      const val = Math.round(minVal + (i * (maxVal - minVal)) / ticks);
      return { y: y(val), label: toFaDigits(String(val)) }; // ← Persianize tick labels
    });
    return {
      pathSafety:   line(trend.map((p) => p.wait_safety_h)),
      pathDelivery: line(trend.map((p) => p.approve_to_delivery_h)),
      pathExec:     line(trend.map((p) => p.execution_h)),
      yTicks: yt,
    };
  }, [trend]);

  const kpi = (label: string, v: number) => (
    <div style={kpiBox}>
      <div style={kpiLabel}>{label}</div>
      {/* show one decimal, Persian digits, and Persian unit */}
      <div style={kpiVal}>{toFaDigits(v.toFixed(1))} دقیقه</div>
    </div>
  );

  return (
    <section style={card} dir="rtl">
      <div style={title}>میانگین زمان پاسخ‌دهی</div>
      <div style={sub}>انتظار تا تایید ایمنی • تایید تا دریافت اسپید • تا اتمام کار (دقیقه)</div>
      {loading && <div style={{ ...sub, color: colors.accent }}>در حال بارگذاری…</div>}
      {err && <div style={{ ...sub, color: colors.danger }}>{err}</div>}

      <div style={kpiWrap}>
        {kpi('انتظار تا تایید ایمنی', summary.wait_safety_h)}
        {kpi('تایید تا دریافت اسپید', summary.approve_to_delivery_h)}
        {kpi('تا اتمام کار', summary.execution_h)}
      </div>

      <div style={chartWrap}>
        {!trend.length ? (
          <div style={{ color: colors.sub, fontSize: S.fsSub, padding: 12, textAlign: 'center' }}>
            داده‌ای برای روند در دسترس نیست.
          </div>
        ) : (
          <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: S.chartH, display: 'block' }} preserveAspectRatio="none">
            <g>
              {yTicks.map((t, i) => (
                <g key={i}>
                  <line x1={pad.l} x2={w - pad.r} y1={t.y} y2={t.y} stroke="rgba(255,255,255,.07)" />
                  <text x={pad.l - 6} y={t.y + 3} textAnchor="end" fontSize="9" fill={colors.sub}>{t.label}</text>
                </g>
              ))}
              <path d={pathSafety}  fill="none" stroke="rgba(96,165,250,.95)" strokeWidth="2" />
              <path d={pathDelivery} fill="none" stroke="rgba(34,211,238,.95)" strokeWidth="2" />
              <path d={pathExec}    fill="none" stroke="rgba(52,211,153,.95)" strokeWidth="2" />
            </g>
          </svg>
        )}
      </div>
    </section>
  );
};

export default ResponseTimeAveragesCard;
